import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import en from '@/lib/i18n/en';
import de from '@/lib/i18n/de';
import sq from '@/lib/i18n/sq';

export const BUNDLES = { en, de, sq };

/** Switcher order: German first (primary market), then English, then Albanian. */
export const LANGUAGES = [de, en, sq].map((b) => ({
  code: b.code,
  label: b.label,
  shortLabel: b.shortLabel,
}));

export const DEFAULT_LANG = 'en';

const STORAGE_KEY = 'rmh_lang';

/**
 * Browser locales we map onto a bundle. Checked in order, so more specific
 * tags win. Albanian ships as `sq`, but Kosovo and North Macedonia browsers
 * often report `sq-XK` / `sq-MK`, and some report the country code alone.
 */
const LOCALE_MATCHERS = [
  { code: 'de', tags: ['de'] },
  { code: 'sq', tags: ['sq', 'als', '-al', '-xk'] },
  { code: 'en', tags: ['en'] },
];

const detectInitialLang = () => {
  // An explicit ?lang= wins over everything: this is what hreflang alternates
  // and shared links point at.
  try {
    const param = new URLSearchParams(window.location.search).get('lang');
    if (param && BUNDLES[param]) return param;
  } catch {
    /* URL unavailable */
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && BUNDLES[stored]) return stored;
  } catch {
    /* storage unavailable */
  }
  try {
    const nav = (window.navigator.languages || [window.navigator.language || ''])
      .join(',')
      .toLowerCase();
    const hit = LOCALE_MATCHERS.find((m) => m.tags.some((tag) => nav.includes(tag)));
    if (hit) return hit.code;
  } catch {
    /* navigator unavailable */
  }
  return DEFAULT_LANG;
};

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  t: en,
  setLang: () => {},
  others: [de, sq],
});

/** Replace {token} placeholders in a translation string. */
export const interpolate = (template, values = {}) =>
  String(template ?? '').replace(/\{(\w+)\}/g, (_, key) =>
    values[key] === undefined || values[key] === null ? '' : String(values[key]),
  );

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() =>
    typeof window === 'undefined' ? DEFAULT_LANG : detectInitialLang(),
  );

  const setLang = useCallback((next) => {
    if (!BUNDLES[next]) return;
    setLangState(next);
  }, []);

  // Persist whatever we ended up on, including a language that arrived via
  // ?lang= so the choice survives the next visit.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* storage unavailable */
    }
  }, [lang]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', BUNDLES[lang].htmlLang);
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      t: BUNDLES[lang],
      setLang,
      /** Every bundle except the active one — used for hreflang alternates. */
      others: Object.values(BUNDLES).filter((b) => b.code !== lang),
    }),
    [lang, setLang],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);

export default LanguageProvider;
